import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  Users,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  Eye,
  ArrowLeft,
  Spinner,
  Warning,
  Package,
  CurrencyDollar,
  ChartLineUp,
  ChartBar
} from '@phosphor-icons/react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const CURRENCY_SYMBOLS = {
  USD: '$',
  XCD: 'EC$',
  JMD: 'J$',
  TTD: 'TT$'
};

const DepartmentHeadPortalPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [pendingPOs, setPendingPOs] = useState([]);
  const [recentlyApproved, setRecentlyApproved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPO, setSelectedPO] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dh-portal/dashboard`, getAuthHeaders());
      setPendingPOs(res.data.pending_pos || []);
      setRecentlyApproved(res.data.recently_approved || []);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (poId) => {
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/purchase-orders/${poId}/dh-approve`,
        { action: 'approve' },
        getAuthHeaders()
      );
      toast.success('PO approved successfully!');
      setSelectedPO(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve PO');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setSubmitting(true);
    try {
      await axios.put(
        `${API_URL}/api/purchase-orders/${selectedPO.id}/dh-approve`,
        { action: 'reject', comment: rejectReason },
        getAuthHeaders()
      );
      toast.success('PO rejected');
      setRejectDialogOpen(false);
      setRejectReason('');
      setSelectedPO(null);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject PO');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#EC4899] border-t-transparent"></div>
      </div>
    );
  }

  // Detail View
  if (selectedPO) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => setSelectedPO(null)} className="p-2">
            <ArrowLeft size={20} />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {selectedPO.po_number}
            </h1>
            <p className="text-slate-500">Review and Approve</p>
          </div>
        </div>

        {/* Threshold Warning */}
        {selectedPO.requires_fc_approval && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <Warning size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Requires Financial Controller Approval After You</p>
              <p className="text-sm text-amber-700 mt-1">
                This PO value (${selectedPO.total_value?.toLocaleString()}) exceeds Threshold 2. 
                After your approval, it will be sent to the Financial Controller for final approval.
              </p>
            </div>
          </div>
        )}

        {/* PO Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Department</p>
            <p className="text-lg font-medium text-slate-800 mt-1">{selectedPO.department || selectedPO.department_name}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Total Value</p>
            <p className="text-xl font-bold text-slate-800 mt-1">
              {CURRENCY_SYMBOLS[selectedPO.currency] || '$'}{selectedPO.total_value?.toLocaleString()}
            </p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Vendor</p>
            <p className="text-lg font-medium text-slate-800 mt-1">{selectedPO.vendor_name}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <p className="text-xs text-slate-500 uppercase font-medium">Required Delivery</p>
            <p className="text-lg font-medium text-slate-800 mt-1">
              {selectedPO.required_delivery_date ? format(new Date(selectedPO.required_delivery_date), 'MMM d, yyyy') : '-'}
            </p>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="font-semibold text-slate-800">Line Items</h2>
          </div>
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Item</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Quantity</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Unit</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Unit Price</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {selectedPO.line_items?.map((item, idx) => (
                <tr key={idx}>
                  <td className="px-4 py-3 font-medium text-slate-800">{item.item_name}</td>
                  <td className="px-4 py-3 text-slate-600">{item.quantity}</td>
                  <td className="px-4 py-3 text-slate-600">{item.unit}</td>
                  <td className="px-4 py-3 text-slate-600">{CURRENCY_SYMBOLS[selectedPO.currency] || '$'}{item.unit_price?.toFixed(2)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{CURRENCY_SYMBOLS[selectedPO.currency] || '$'}{item.total_price?.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Approval Audit Trail */}
        {selectedPO.approval_audit_trail?.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-100 p-4">
            <h2 className="font-semibold text-slate-800 mb-3">Approval History</h2>
            <div className="space-y-2">
              {selectedPO.approval_audit_trail.map((entry, idx) => (
                <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                  <CheckCircle size={20} className="text-green-500" />
                  <div>
                    <p className="font-medium text-slate-800">{entry.approver_name} ({entry.approver_role})</p>
                    <p className="text-xs text-slate-500">
                      {entry.action} - {entry.timestamp ? format(new Date(entry.timestamp), 'MMM d, yyyy HH:mm') : '-'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <Button
            variant="outline"
            onClick={() => {
              setRejectDialogOpen(true);
            }}
            className="border-red-300 text-red-600 hover:bg-red-50"
            data-testid="dh-reject-btn"
          >
            <XCircle size={18} className="mr-2" />
            Reject with Note
          </Button>
          <Button
            onClick={() => handleApprove(selectedPO.id)}
            disabled={submitting}
            className="bg-[#EC4899] hover:bg-[#DB2777]"
            data-testid="dh-approve-btn"
          >
            {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : <CheckCircle size={18} className="mr-2" />}
            Approve PO
          </Button>
        </div>

        {/* Reject Dialog */}
        <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Reject Purchase Order</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Rejection Reason (Required)</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide a reason for rejecting this PO..."
                  rows={4}
                  data-testid="reject-reason-input"
                />
                <p className="text-xs text-slate-500">
                  This note will be permanently recorded and sent to the Purchasing Manager.
                </p>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setRejectDialogOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleReject}
                  disabled={submitting}
                  className="bg-red-600 hover:bg-red-700"
                >
                  {submitting ? <Spinner size={18} className="animate-spin mr-2" /> : null}
                  Confirm Rejection
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Dashboard View
  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-xl bg-[#0F52BA]/10 flex items-center justify-center">
          <Users size={24} className="text-[#0F52BA]" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Department Head Portal
          </h1>
          <p className="text-slate-500">Welcome, {user?.full_name}</p>
        </div>
      </div>

      {/* Key Actions - 2 Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-[#0F52BA] to-[#3B82F6] text-white rounded-xl p-6">
          <Clock size={32} className="mb-3" />
          <h3 className="text-xl font-bold">Approve Requisitions</h3>
          <p className="text-white/80 text-sm mt-1">{pendingPOs.length} POs awaiting your approval</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-100 p-6">
          <ChartLineUp size={32} className="text-[#0F52BA] mb-3" />
          <h3 className="text-xl font-bold text-slate-800">View Spend</h3>
          <p className="text-slate-500 text-sm mt-1">{recentlyApproved.length} recently approved</p>
        </div>
      </div>

      {/* Pending Approvals */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 bg-[#EC4899]/5">
          <h2 className="text-lg font-semibold text-slate-800">POs Awaiting Your Approval</h2>
          <p className="text-sm text-slate-500">Purchase orders that require Department Head approval</p>
        </div>

        {pendingPOs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
            <p>No POs pending your approval</p>
            <p className="text-sm mt-2">You're all caught up!</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Department</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Total Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Next Step</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pendingPOs.map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50" data-testid={`dh-po-row-${po.id}`}>
                    <td className="px-4 py-4 font-medium text-slate-800">{po.po_number}</td>
                    <td className="px-4 py-4 text-slate-600">{po.department || po.department_name}</td>
                    <td className="px-4 py-4 text-slate-600">{po.vendor_name}</td>
                    <td className="px-4 py-4 font-medium text-slate-800">
                      {CURRENCY_SYMBOLS[po.currency] || '$'}{po.total_value?.toLocaleString()}
                    </td>
                    <td className="px-4 py-4">
                      {po.requires_fc_after_dh ? (
                        <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                          Then FC Approval
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          Issue to Vendor
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => setSelectedPO(po)}
                        data-testid={`review-po-${po.id}`}
                      >
                        <Eye size={18} className="mr-1" />
                        Review
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recently Approved */}
      {recentlyApproved.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-slate-800">Recently Approved by You</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">PO Number</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Vendor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Approved At</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recentlyApproved.slice(0, 5).map(po => (
                  <tr key={po.id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 font-medium text-slate-800">{po.po_number}</td>
                    <td className="px-4 py-3 text-slate-600">{po.vendor_name}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">
                      {CURRENCY_SYMBOLS[po.currency] || '$'}{po.total_value?.toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {po.dh_approved_at ? format(new Date(po.dh_approved_at), 'MMM d, HH:mm') : '-'}
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        {po.status === 'po_issued' ? 'Issued' : po.status === 'pending_fc_approval' ? 'With FC' : po.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reports Card - At the bottom of DH Dashboard */}
      <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-6" data-testid="reports-card">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-xl bg-[#3D1D72]/10 flex items-center justify-center">
            <ChartBar size={28} className="text-[#3D1D72]" weight="duotone" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-800">Reports</h3>
            <p className="text-sm text-slate-500">View Spend by Department report for your department</p>
          </div>
          <Button 
            onClick={() => window.location.href = '/reports'}
            className="bg-[#3D1D72] hover:bg-[#2D1552]"
            data-testid="open-reports-btn"
          >
            Open Reports
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DepartmentHeadPortalPage;
